import type { BMode } from "../lib/adhd";
import { useLang } from "../lib/i18n";
import { adhdCopy } from "../lib/i18n/adhd";

// Variant B · Sygnał — "can I eat?"
// A full-screen traffic-light: one decision word + a short hint. Works at a
// glance. The `stop` state uses graphite, never alarm red
// (design handoff §"2-4 · Wariant B — Sygnał", §"Tone of voice").
//
// Body-only: the wrapper, header (logo + pill), chooser and nav are owned by
// HomeMaybeADHD. Background/colour palette of the wrapper is also driven by
// the parent so it can transition smoothly between bModes.

export type BPalette = {
  ink: string;
  hint: string;
  dot: string;
};

const MODE_ORDER: BMode[] = ["ok", "soft", "stop"];

type Props = {
  bMode: BMode;
  // Manual session-only override (tap a state bar). Resets at midnight.
  onModeChange: (m: BMode) => void;
  // Remaining kcal — shown in the hint sentence for ok/soft.
  kcalRemaining: number;
  // Whether any meal has been logged today (drives the empty state).
  hasData: boolean;
  // Foreground palette from the parent (palette varies with bMode).
  palette: BPalette;
};

export function HomeAdhdB({ bMode, onModeChange, kcalRemaining, hasData, palette }: Props) {
  const { lang } = useLang();
  const t = adhdCopy[lang];

  // Fresh user with no data: show the neutral "start" word + sub regardless
  // of the computed state (design handoff §"Empty/edge states").
  const word = !hasData
    ? t.bWordStart
    : bMode === "ok"
      ? t.bWordOk
      : bMode === "soft"
        ? t.bWordSoft
        : t.bWordStop;
  const sub = !hasData
    ? t.bSubStart
    : bMode === "ok"
      ? t.bSubOk(kcalRemaining)
      : bMode === "soft"
        ? t.bSubSoft(kcalRemaining)
        : t.bSubStop;

  // 6+ char words (POMAŁU) drop to 76px so they still fit one line.
  const wordSize = word.length >= 6 ? 76 : 102;
  const stateAria: Record<BMode, string> = {
    ok: t.bStateAriaOk,
    soft: t.bStateAriaSoft,
    stop: t.bStateAriaStop,
  };

  return (
    <div
      className="flex flex-col"
      style={{ flex: 1, justifyContent: "center", gap: 16, marginTop: -20 }}
    >
      <div
        aria-hidden
        style={{
          width: 16,
          height: 16,
          borderRadius: 99,
          background: palette.dot,
          boxShadow: `0 0 0 6px ${palette.dot}22`,
        }}
      />

      <div
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 900,
          fontSize: wordSize,
          lineHeight: 0.88,
          letterSpacing: "-0.05em",
          color: palette.ink,
        }}
      >
        {word}
        <span style={{ color: palette.dot }}>.</span>
      </div>

      <p
        style={{
          fontSize: 19,
          color: palette.hint,
          fontWeight: 500,
          lineHeight: 1.35,
          maxWidth: 300,
          textWrap: "pretty",
        }}
      >
        {sub}
      </p>

      {/* State bars — manual session-only override (resets at midnight). */}
      <div className="flex" style={{ gap: 6, marginTop: 8 }}>
        {MODE_ORDER.map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => onModeChange(m)}
            aria-label={stateAria[m]}
            aria-pressed={m === bMode}
            style={{
              width: 28,
              height: 6,
              borderRadius: 99,
              border: "none",
              padding: 0,
              cursor: "pointer",
              touchAction: "manipulation",
              background:
                m === bMode
                  ? palette.ink
                  : bMode === "stop"
                    ? "rgba(255,255,255,0.18)"
                    : "rgba(0,0,0,0.12)",
            }}
          />
        ))}
      </div>
    </div>
  );
}
